import React from 'react';
import HurglassOutlineIcon from '@assets/icons/table/hourglass-outline.svg';
import ShowCertificateIcon from '@assets/icons/table/show-certificate.svg';
import { BaseTooltip } from '@components';
import { msg } from '@lingui/core/macro';
import { TooltipContent } from '@routes/___shared___/styled.tsx';
import {
    CERTIFICATE_URL_NAME,
    PENDING_CERTIFICATE_NAME,
} from '@routes/profile-bar/___pages___/my-results/ui/tables/my-learning/constants.ts';
import type { UserPreferencesSettings } from '@types';
import { formatDateBasedOnPreferences } from '@utils/date.ts';
import { isEqualIgnoreCase } from '@utils/text.ts';
import { translateItem } from '@utils/translations.ts';

import Action from './action';
import { STATUS_TO_TYPE, STATUS_TYPE } from './constants.ts';
import ResultsEmptyState from './results-empty-state';
import Status from './status';
import { CellItem, CenteredWrapper, CertificateWrapper, EmptyRow, Row } from './styled.tsx';
import type { ActionType, StatusType, TableCell, TableCellValue, TableRow } from './types.ts';

const LARGE_COLUMN_WIDTH = 5;
const SMALL_COLUMN_WIDTH = 1;
const REGULAR_COLUMN_WIDTH = 3;

type Renderer = (cell: TableCell, userPreferencesSettings: UserPreferencesSettings) => React.ReactNode;

export const getStatusType = (value: TableCellValue): StatusType =>
    STATUS_TO_TYPE.find((item) => isEqualIgnoreCase(item.status, String(value ?? '')))?.type ?? STATUS_TYPE.IN_PROGRESS;

const renderCertificate = (cell: TableCell) => {
    let certificateColumn = <>-</>; // no certificate
    const hasGeneratedCertificate = cell.name === CERTIFICATE_URL_NAME && cell.value;
    const handleOnClick = () => {
        if (hasGeneratedCertificate && cell.onclick !== undefined) {
            cell.onclick();
        }
    };
    if (cell.name === PENDING_CERTIFICATE_NAME) {
        certificateColumn = (
            <BaseTooltip
                title={<TooltipContent>{translateItem(msg`Report is being generated - please wait.`)}</TooltipContent>}
                placement="right"
                arrow={false}
                icon={<HurglassOutlineIcon />}
            />
        );
    }
    if (hasGeneratedCertificate) {
        certificateColumn = <ShowCertificateIcon />;
    }
    return <CertificateWrapper onClick={handleOnClick}>{certificateColumn}</CertificateWrapper>;
};

const renderers: Record<TableCell['type'], Renderer> = {
    status: (cell) => <Status type={getStatusType(cell.value)} label={cell.value as string} />,
    text: (cell) => <div>{cell.value as string}</div>,
    certificate: (cell) => renderCertificate(cell),
    score: (cell) => <div>{cell ? (cell.value as string) : '-'}</div>,
    date: (cell, userPreferencesSettings) => (
        <div>
            {cell
                ? formatDateBasedOnPreferences(cell.value as string, userPreferencesSettings.preferredDateFormat)
                : ''}
        </div>
    ),
    action: (cell) => <Action type={cell.value as ActionType} />,
};

export const renderCell = (
    cell: TableCell,
    columnType: TableCell['type'],
    userPreferencesSettings: UserPreferencesSettings,
): React.ReactNode => {
    const renderer = renderers[columnType];

    return renderer ? renderer(cell, userPreferencesSettings) : null;
};

export const calculateColumnAndCellWidths = (
    columns: TableCell[],
    fixedColumnFlexes?: Record<string, number>,
): Record<string, string> => {
    const defaultFlex = REGULAR_COLUMN_WIDTH;
    const flexMap = columns.map((col, index) => ({
        id: col.name,
        flex:
            fixedColumnFlexes?.[col.name] ??
            (index === 0 ? LARGE_COLUMN_WIDTH : index === columns.length - 1 ? SMALL_COLUMN_WIDTH : defaultFlex),
    }));

    const totalFlex = flexMap.reduce((sum, col) => sum + col.flex, 0);

    const widthMap: Record<string, string> = {};
    flexMap.forEach((col) => {
        widthMap[col.id] = `${(col.flex / totalFlex) * 100}%`;
    });

    return widthMap;
};

export const shouldAlignToRight = (rightAlignmentColumns: string[], columnId: string) =>
    rightAlignmentColumns.includes(columnId);

export const renderDataOrEmptyState = (
    rows: TableRow[],
    headerLength: number,
    widths: Record<string, string>,
    activeFilters: string[],
    searchTerm: string,

    searchStateTexts: {
        noFiltersNoDataHeaderText: string;
        noFiltersNoDataSubtitleText: string;
        filtersNoDataHeaderText: string;
        filtersNoDataSubtitleText: string;
    },
    rightAlignmentColumns: string[],
    userPreferencesSettings: UserPreferencesSettings,
) => {
    const {
        noFiltersNoDataHeaderText,
        noFiltersNoDataSubtitleText,
        filtersNoDataHeaderText,
        filtersNoDataSubtitleText,
    } = searchStateTexts;
    if (rows.length === 0) {
        const header = activeFilters.length > 0 || searchTerm ? filtersNoDataHeaderText : noFiltersNoDataHeaderText;
        const subtitle =
            activeFilters.length > 0 || searchTerm ? filtersNoDataSubtitleText : noFiltersNoDataSubtitleText;

        return (
            <EmptyRow>
                <td colSpan={headerLength}>
                    <CenteredWrapper>
                        <ResultsEmptyState header={header} subtitle={subtitle} />
                    </CenteredWrapper>
                </td>
            </EmptyRow>
        );
    }

    return rows.map((row) => (
        <Row key={row.key}>
            {row.cells.map((cell) => {
                return (
                    <CellItem
                        key={cell.name}
                        width={widths[cell.name]}
                        $shouldAlignToRight={shouldAlignToRight(rightAlignmentColumns, cell.name)}
                    >
                        {renderCell(cell, cell.type, userPreferencesSettings)}
                    </CellItem>
                );
            })}
        </Row>
    ));
};
